/**
 * 前端版本更新检测（参照资源地图项目 ep_pedestal/src/util/versionUpdate.js 移植）
 * 对比本地构建号与线上 version.json，发现不一致则提示刷新
 */
import { ElMessageBox } from "element-plus";

const LOCAL_VERSION = process.env.VUE_APP_BUILD_ID || "";
/** 轮询间隔：5 分钟 */
const CHECK_INTERVAL = 5 * 60 * 1000;
/** 用户点「稍后」后，再次提醒的间隔 */
const REMIND_LATER_INTERVAL = 10 * 60 * 1000;

let prompting = false;
let timer = null;

/**
 * 拼接版本文件地址（优先与 index.html 同目录的资源根路径）
 */
function resolveVersionUrls() {
  const stamp = Date.now();
  const urls = [];

  const pushUrl = (base) => {
    if (!base) return;
    try {
      const normalized = base.endsWith("/") ? base : `${base}/`;
      const fromBase = new URL("version.json", normalized);
      urls.push(`${fromBase.href}?t=${stamp}`);
    } catch (e) {
      urls.push(`${String(base).replace(/\/?$/, "/")}version.json?t=${stamp}`);
    }
  };

  // 1. Vue CLI BASE_URL
  pushUrl(process.env.BASE_URL || "/");

  // 2. 从已加载的 static/js 脚本反推（兜底）
  const scripts = document.getElementsByTagName("script");
  for (let i = 0; i < scripts.length; i++) {
    const src = scripts[i].src || "";
    if (/\/static\/js\//.test(src)) {
      urls.push(src.replace(/\/static\/js\/[^/?#]+.*$/, `/version.json?t=${stamp}`));
      break;
    }
  }

  // 去重
  return urls.filter((url, index, arr) => arr.indexOf(url) === index);
}

/**
 * 拉取远程版本号
 */
async function fetchRemoteVersion() {
  const urls = resolveVersionUrls();
  for (let i = 0; i < urls.length; i++) {
    try {
      const res = await fetch(urls[i], { cache: "no-store" });
      if (!res.ok) continue;
      const data = await res.json();
      if (data && data.version) {
        return data.version;
      }
    } catch (e) {
      // 尝试下一个地址
    }
  }
  return null;
}

/**
 * 弹出更新提示
 */
function showUpdateDialog() {
  if (prompting) return;
  prompting = true;
  ElMessageBox.confirm(
    "检测到系统已更新，请刷新页面以使用最新版本。",
    "版本更新",
    {
      confirmButtonText: "立即刷新",
      cancelButtonText: "稍后",
      type: "warning",
      closeOnClickModal: false,
      closeOnPressEscape: false,
      distinguishCancelAndClose: true,
    }
  )
    .then(() => {
      window.location.reload();
    })
    .catch(() => {
      // 稍后：一段时间后再提醒
      setTimeout(() => {
        prompting = false;
      }, REMIND_LATER_INTERVAL);
    });
}

/**
 * 执行一次版本检测
 */
async function checkVersionUpdate() {
  if (!LOCAL_VERSION || prompting) return;
  const remote = await fetchRemoteVersion();
  if (remote && remote !== LOCAL_VERSION) {
    showUpdateDialog();
  }
}

/**
 * 启动版本检测：定时 + 切回前台
 * 开发环境不启用，避免 HMR 干扰
 */
export function startVersionCheck() {
  if (process.env.NODE_ENV === "development") return;
  if (!LOCAL_VERSION) return;

  checkVersionUpdate();

  if (timer) {
    clearInterval(timer);
  }
  timer = setInterval(checkVersionUpdate, CHECK_INTERVAL);

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") {
      checkVersionUpdate();
    }
  });
}
